// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Alarm card for the Alarms / Findings tabs, in tile or list layout, matching
// the Operator Console mock. Two chips (severity + stage), a group glyph, a
// mono meta line `14:25 (19:15 ago) · Chiller Plant`, and the dxTag summary
// (tile) / gradient dx pill (list). Left accent bar = severity accent.

import React from 'react';
import type { Alarm } from '../../../types/alarm';
import { severityAccent } from '../../../theme/severity';
import { SeverityChip } from './SeverityChip';
import { StageChip } from './StageChip';
import { GroupIcon } from './GroupIcon';
import { ComplianceChip, WorkStatusChip, complianceStyle } from './AlarmKindChips';
import type { AlarmLayout } from '../../../stores/alarmsViewStore';
import { formatElapsedMinutes } from '../../../utils/time';
import { useSiteTime } from '../../../hooks/useSiteTime';
import { locationShort } from '../../../utils/location';
import { AlarmOwnerLine } from './AlarmOwnerLine';

export interface AlarmCardProps {
  alarm: Alarm;
  layout: AlarmLayout;
  /**
   * Open handler. When omitted the card renders non-interactive (a plain `div`,
   * no click affordance).
   */
  onOpen?: (_id: string) => void;
  /**
   * Kind chip ("Alarm", "Routine", …) leading the card's chips. The mixed
   * closed queue sets it; single-kind tabs don't need one.
   */
  kindLabel?: string;
  /**
   * Render as a closed-queue record: a "Closed" chip instead of the stage, the
   * close time instead of the raise clock and elapsed clause, the domain
   * instead of the location. Only Home › Closed sets it — a closed finding on
   * the Anomalies tab keeps its raise time.
   */
  closedRecord?: boolean;
}

/** The kind chip the mixed closed queue leads every card with. */
export function KindChip({ label }: { label: string }) {
  return (
    <span className="hs-kind-label" data-testid="kind-chip">
      {label}
    </span>
  );
}

function DxPill({ text }: { text: string }) {
  return (
    <span className="hs-dx-pill">
      <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 1.4l1.6 4.4 4.4 1.6-4.4 1.6L8 13.4 6.4 9 2 7.4l4.4-1.6z" />
      </svg>
      {text}
    </span>
  );
}

/**
 * Shared tile-card shell — button, accent bar, head/asset/meta/dx layout —
 * used by the alarm tile, RoutineCard, and RecommendationCard alike so the
 * three don't each hand-roll the same button/div skeleton.
 */
function AlarmTileShell({
  accent,
  opacity = 1,
  onOpen,
  dataAttrs,
  kindLabel,
  head,
  asset,
  meta,
  dx,
  owner,
}: {
  accent: string;
  opacity?: number;
  /** Omit to render a non-interactive tile (plain div, no click). */
  onOpen?: () => void;
  dataAttrs?: Record<string, string>;
  kindLabel?: string;
  head: React.ReactNode;
  asset: React.ReactNode;
  meta: React.ReactNode;
  dx?: React.ReactNode;
  owner?: React.ReactNode;
}) {
  const inner = (
    <>
      <div className="hs-alarm-card-head">
        {kindLabel && <KindChip label={kindLabel} />}
        {head}
      </div>
      <div className="hs-alarm-card-asset">{asset}</div>
      <div className="hs-alarm-card-meta">{meta}</div>
      {owner}
      {dx && <div className="hs-alarm-card-dx">{dx}</div>}
    </>
  );
  const style = { borderLeftColor: accent, opacity };

  if (!onOpen) {
    return (
      <div
        className="hs-alarm-card hs-alarm-card--tile hs-alarm-card--static"
        style={style}
        data-testid="alarm-card"
        {...dataAttrs}
      >
        {inner}
      </div>
    );
  }

  return (
    <button
      type="button"
      className="hs-alarm-card hs-alarm-card--tile"
      style={style}
      onClick={onOpen}
      data-testid="alarm-card"
      {...dataAttrs}
    >
      {inner}
    </button>
  );
}

/** The raise clock on the alarm's own site: a fleet-wide list spans sites. */
function useAlarmClock(alarm: Alarm): string {
  const { formatIn } = useSiteTime();
  return formatIn(alarm.siteId, alarm.eventTimestamp, 'clock', 'Unknown');
}

/** Routine card — compliance chip + cadence/result (mock 5c). */
function RoutineCard({
  alarm,
  onOpen,
  kindLabel,
}: {
  alarm: Alarm;
  onOpen: (_id: string) => void;
  kindLabel?: string;
}) {
  return (
    <AlarmTileShell
      kindLabel={kindLabel}
      accent={complianceStyle(alarm.compliance).accent}
      onOpen={() => onOpen(alarm.id)}
      dataAttrs={{ 'data-kind': 'routine' }}
      head={
        <>
          <ComplianceChip compliance={alarm.compliance} />
          <span className="hs-alarm-card-ruleid">{alarm.ruleId}</span>
        </>
      }
      asset={alarm.asset}
      meta={
        <>
          {alarm.cadence && <span>{alarm.cadence}</span>}
          <span className="hs-dot-sep">·</span>
          <span className="hs-alarm-card-loc">{locationShort(alarm.location)}</span>
        </>
      }
      dx={alarm.result}
    />
  );
}

/** Recommendation card — approved status + benefit metrics (mock 5d). */
function RecommendationCard({
  alarm,
  onOpen,
  kindLabel,
}: {
  alarm: Alarm;
  onOpen: (_id: string) => void;
  kindLabel?: string;
}) {
  const clock = useAlarmClock(alarm);
  return (
    <AlarmTileShell
      kindLabel={kindLabel}
      accent="var(--interactive)"
      onOpen={() => onOpen(alarm.id)}
      dataAttrs={{ 'data-kind': 'optwork' }}
      head={<WorkStatusChip status={alarm.ownerStatus} />}
      asset={alarm.asset}
      meta={
        <>
          <span>{clock}</span>
          {alarm.optBenefit && (
            <>
              <span className="hs-dot-sep">·</span>
              <span className="hs-opt-benefit">{alarm.optBenefit}</span>
            </>
          )}
        </>
      }
      dx={alarm.optAction || alarm.taskTitle}
    />
  );
}

export function AlarmCard({ alarm, layout, onOpen, kindLabel, closedRecord }: AlarmCardProps) {
  const raisedClock = useAlarmClock(alarm);
  const { formatIn } = useSiteTime();
  const kind = alarm.kind ?? 'alarm';
  // Routine / recommendation cards are always interactive (their tabs and the
  // closed queue both open them); a noop keeps their required onOpen satisfied.
  const noop = () => {};
  if (kind === 'routine') {
    return <RoutineCard alarm={alarm} onOpen={onOpen ?? noop} kindLabel={kindLabel} />;
  }
  if (kind === 'optwork') {
    return <RecommendationCard alarm={alarm} onOpen={onOpen ?? noop} kindLabel={kindLabel} />;
  }

  const accent = severityAccent(alarm.sev);
  // A closed-queue record shows when it closed (date and time, on the site
  // clock) instead of the raise clock and the "… ago" elapsed clause, and its
  // stage reads plainly "Closed". AEM is only now adding `closed_at`; a row
  // without it reads "Closed —" rather than standing in another instant.
  const asRecord = Boolean(closedRecord && alarm.closed);
  const clock = asRecord
    ? `Closed ${formatIn(alarm.siteId, alarm.closedAt, 'dateTimeShort', '\u2014')}`
    : raisedClock;
  // event_timestamp (AEM 0.2.0) may still be null for older/synthetic rows —
  // omit the "… ago" clause rather than fabricating an elapsed time.
  const elapsed =
    !asRecord && alarm.raisedAgo != null ? formatElapsedMinutes(alarm.raisedAgo) : null;
  const stageChip = asRecord ? (
    <span className="hs-stage-chip hs-stage-chip--closed" data-stage="closed">
      Closed
    </span>
  ) : (
    <StageChip alarm={alarm} />
  );
  const dim = alarm.closed ? 0.5 : 1;
  // Non-interactive when no onOpen is given.
  const handleOpen = onOpen ? () => onOpen(alarm.id) : undefined;

  if (layout === 'tiles') {
    return (
      <AlarmTileShell
        accent={accent}
        opacity={dim}
        onOpen={handleOpen}
        dataAttrs={{ 'data-sev': alarm.sev }}
        kindLabel={kindLabel}
        head={
          <>
            <SeverityChip sev={alarm.sev} />
            {stageChip}
            <span className="hs-alarm-card-groupicon" title={alarm.group}>
              <GroupIcon group={alarm.group} size={14} />
            </span>
          </>
        }
        asset={alarm.asset}
        meta={
          <>
            <span>{clock}</span>
            {elapsed && <span>({elapsed} ago)</span>}
            <span className="hs-dot-sep">·</span>
            <span className="hs-alarm-card-loc">
              {asRecord && alarm.group ? alarm.group : locationShort(alarm.location)}
            </span>
          </>
        }
        dx={alarm.dxTag}
        owner={<AlarmOwnerLine owner={alarm.owner} />}
      />
    );
  }

  const RowTag = handleOpen ? 'button' : 'div';
  return (
    <RowTag
      {...(handleOpen ? { type: 'button' as const, onClick: handleOpen } : {})}
      className={`hs-alarm-row${handleOpen ? '' : ' hs-alarm-row--static'}`}
      style={{ borderLeftColor: accent, opacity: dim }}
      data-testid="alarm-card"
      data-sev={alarm.sev}
    >
      <span className="hs-alarm-row-chips">
        {kindLabel && <KindChip label={kindLabel} />}
        <SeverityChip sev={alarm.sev} />
        {stageChip}
      </span>
      <div className="hs-alarm-row-title">
        <span className="hs-alarm-card-asset">{alarm.asset}</span>
        <span className="hs-alarm-row-loc">{locationShort(alarm.location)}</span>
        <span className="hs-alarm-row-group">
          <GroupIcon group={alarm.group} size={13} />
          {alarm.group}
        </span>
      </div>
      <span className="hs-alarm-row-chevron" aria-hidden="true">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
        >
          <path d="M6 3l5 5-5 5" />
        </svg>
      </span>
      <div className="hs-alarm-row-meta">
        <span className="hs-alarm-row-time">{clock}</span>
        {elapsed && <span className="hs-alarm-row-elapsed">({elapsed} ago)</span>}
        {alarm.src && <span className="hs-alarm-row-src">{alarm.src}</span>}
        {alarm.quality === 'suspect' && (
          <span className="hs-alarm-row-suspect" title="Data quality: suspect">
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
            >
              <path d="M8 2.5l6 11H2z" strokeLinejoin="round" />
              <path d="M8 6.6v3M8 11.4v.5" strokeLinecap="round" />
            </svg>
            suspect
          </span>
        )}
        <AlarmOwnerLine owner={alarm.owner} inline />
        <span className="hs-alarm-row-spacer" />
        {alarm.dxTag && <DxPill text={alarm.dxTag} />}
      </div>
    </RowTag>
  );
}

export default AlarmCard;
